import {useState, useEffect, useRef} from 'react';

import classnames from 'classnames';

import type {GetSessionUser_loggedInUser_sendableEmailAddresses as UserSendableEmailAddress} from 'nutshell-graphql-types';

import {Button} from '../../../button/button';
import {PaperPlane} from '../../../icon';
import {Stack} from '../../../layout/stack/stack';

import {useGetTeams} from '../../../../../../../ui/core/graphql/hooks/use-get-teams';

import {EmailInputFooter} from './email-input-footer';
import {InboxRichTextInput} from './inbox-rich-text-input';
import {SendButton} from './send-button';
import {
    MODE,
    type Mode,
    type SendInput,
    type ImageHandlingProps,
    type SenderAddress,
} from './types';

import styles from './message-input.module.css';

interface Props extends ImageHandlingProps {
    onSend: (input: {
        content: string;
        snoozeDuration?: number;
        closeThread?: boolean;
        imageUrls?: string[];
        fromAddress?: SenderAddress;
    }) => Promise<any>;
    onNoteCreate: (note: string) => Promise<any>;
    isSending: boolean;
    showSnoozeOptions?: boolean;
    cantSendMessage: string | null | undefined;
    signature?: string | null | undefined;
    placeholder: string;
    notifyTyping: () => void;
    userSendableEmailAddresses?: UserSendableEmailAddress[] | null | undefined;
}

export function EmailMessageInput(props: Props) {
    const {teams} = useGetTeams();
    const [value, setValue] = useState('');
    const [htmlValue, setHtmlValue] = useState('');
    const [selectedSender, setSelectedSender] = useState<SenderAddress | null>(null);
    const [mode, setMode] = useState<Mode>(MODE.EMAIL);
    const [isMessageSending, setIsMessageSending] = useState(false);
    const [useSignature, setUseSignature] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const isInputDisabled = Boolean(props.cantSendMessage || isMessageSending);
    const hasValidContent = value.trim().length > 0;
    const isSendDisabled = (!hasValidContent && !props.hasImages) || isMessageSending;

    useEffect(() => {
        if (props.cantSendMessage) {
            setValue('');
            setHtmlValue('');
        }
    }, [props.cantSendMessage]);

    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        const handleMouseDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsExpanded(false);
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isExpanded]);

    const handleSend = async (input?: SendInput | null | undefined) => {
        setIsMessageSending(true);
        try {
            const allImageUrls = await props.collectAllImageUrls();
            const shouldAppendSignature =
                useSignature && props.signature && props.signature.trim().length > 0;
            const content = shouldAppendSignature
                ? `${htmlValue}<div><br></div>${props.signature}`
                : htmlValue;

            await props.onSend({
                content,
                ...input,
                imageUrls: allImageUrls.length > 0 ? allImageUrls : undefined,
                fromAddress: selectedSender || undefined,
            });
            setValue('');
            setHtmlValue('');
            setIsExpanded(false);
            props.clearAllImages();
        } finally {
            setIsMessageSending(false);
        }
    };

    const handleNoteCreate = async () => {
        setIsMessageSending(true);
        try {
            await props.onNoteCreate(value);
            setValue('');
            setHtmlValue('');
        } finally {
            setIsMessageSending(false);
        }
    };

    const handleRichTextChange = (html: string, text: string) => {
        setHtmlValue(html);
        setValue(text);
        props.notifyTyping();
    };

    return (
        <div ref={containerRef}>
            <InboxRichTextInput
                value={htmlValue}
                onChange={handleRichTextChange}
                placeholder={props.placeholder}
                isDisabled={isInputDisabled}
                teams={teams}
                className={classnames({
                    [styles['input-container']]: props.hasImages,
                })}
                selectedSender={selectedSender}
                setSelectedSender={setSelectedSender}
                mode={mode}
                setMode={setMode}
                signature={props.signature}
                useSignature={useSignature}
                setUseSignature={setUseSignature}
                clearAllImages={props.clearAllImages}
                userSendableEmailAddresses={props.userSendableEmailAddresses}
                isExpanded={isExpanded}
                onExpand={() => setIsExpanded(true)}
                previewText={value}
                collapsedFooter={
                    mode === MODE.EMAIL ? (
                        <SendButton
                            isDisabled={isSendDisabled || !selectedSender}
                            onSend={handleSend}
                            isSending={props.isSending}
                            showSnoozeOptions={props.showSnoozeOptions}
                        />
                    ) : (
                        <Button
                            variant='primary'
                            size='big'
                            onClick={handleNoteCreate}
                            disabled={isSendDisabled}
                        >
                            <Stack isHorizontal={true} spacing={8} alignItems='center'>
                                <PaperPlane size={14} />
                                {isMessageSending ? 'Adding…' : 'Add note'}
                            </Stack>
                        </Button>
                    )
                }
                footer={
                    <EmailInputFooter
                        isSendDisabled={isSendDisabled || (mode === MODE.EMAIL && !selectedSender)}
                        onNoteCreate={handleNoteCreate}
                        onSend={handleSend}
                        isSending={props.isSending}
                        showSnoozeOptions={props.showSnoozeOptions}
                        mode={mode}
                    />
                }
            />
        </div>
    );
}
